import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShoppingCart, User, Bell, LogOut, Package } from "lucide-react";
import { ZafaraFullLogo } from "@/components/landing/Logo";
import { useShop } from "@/context/ShopContext";

const NAV_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/pourquoi-zafara", label: "Pourquoi Zafara" },
  { to: "/application", label: "L'application" },
  { to: "/contact", label: "Contact" },
];

export const ShopHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuAcc, setMenuAcc] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, cart, unread, logout } = useShop();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const itemCount = cart.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FBFAF6]/95 border-b border-[#EDE7D9] shadow-[0_4px_20px_rgba(14,122,59,0.06)]"
          : "backdrop-blur-md bg-[#FBFAF6]/80 border-b border-[#EDE7D9]/60"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link to="/" data-testid="shop-nav-logo" className="hover:-translate-y-0.5 transition-transform">
            <ZafaraFullLogo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`shop-nav-${l.to.replace(/\//g, "-") || "home"}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(l.to) ? "text-[#0E7A3B]" : "text-[#16241B]/85 hover:text-[#0E7A3B]"
                }`}
              >
                {l.label}
                {isActive(l.to) && <span className="block h-0.5 w-full mt-1 bg-[#F5A800] rounded-full" />}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link
              to="/panier"
              data-testid="shop-nav-cart"
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EDE7D9] bg-white hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
              aria-label="Panier"
            >
              <ShoppingCart className="h-4 w-4" />
              {itemCount > 0 && (
                <span
                  data-testid="shop-cart-badge"
                  className="absolute -top-1 -right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E8112D] px-1 text-[10px] font-bold text-white"
                >
                  {itemCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="relative">
                <button
                  data-testid="shop-nav-account"
                  onClick={() => setMenuAcc((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0E7A3B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0d6a34] transition-colors"
                >
                  <User className="h-4 w-4" />
                  {user.name?.split(" ")[0] || "Compte"}
                  {unread > 0 && (
                    <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#F5A800] px-1 text-[10px] font-bold text-[#16241B]">
                      {unread}
                    </span>
                  )}
                </button>
                {menuAcc && (
                  <div
                    onMouseLeave={() => setMenuAcc(false)}
                    className="absolute right-0 mt-2 w-56 rounded-2xl border border-[#EDE7D9] bg-white shadow-[0_20px_60px_rgba(22,36,27,0.15)] p-2 z-50"
                  >
                    <Link
                      to="/mes-commandes"
                      onClick={() => setMenuAcc(false)}
                      data-testid="shop-nav-orders"
                      className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-[#FBFAF6]"
                    >
                      <Package className="h-4 w-4" /> Mes commandes
                    </Link>
                    <Link
                      to="/suivi"
                      onClick={() => setMenuAcc(false)}
                      className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-[#FBFAF6]"
                    >
                      <Bell className="h-4 w-4" /> Suivi de livraison
                    </Link>
                    <button
                      data-testid="shop-nav-logout"
                      onClick={() => { logout(); setMenuAcc(false); navigate("/"); }}
                      className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-[#FBFAF6] text-[#E8112D]"
                    >
                      <LogOut className="h-4 w-4" /> Se déconnecter
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/compte/connexion"
                  data-testid="shop-nav-login"
                  className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-4 py-2 text-sm font-medium hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
                >
                  Se connecter
                </Link>
                <Link
                  to="/compte/inscription"
                  data-testid="shop-nav-signup"
                  className="inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-4 py-2 text-sm font-semibold hover:bg-[#0E7A3B] hover:text-white transition-colors"
                >
                  Créer un compte
                </Link>
              </>
            )}
          </div>

          <button
            data-testid="shop-nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#EDE7D9] bg-white text-[#16241B]"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 border-t border-[#EDE7D9] pt-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`block w-full text-left text-base font-medium py-2.5 ${
                  isActive(l.to) ? "text-[#0E7A3B]" : "text-[#16241B]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/panier"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between py-2.5 text-base font-medium"
            >
              <span className="inline-flex items-center gap-2"><ShoppingCart className="h-4 w-4" /> Panier</span>
              {itemCount > 0 && <span className="text-xs bg-[#E8112D] text-white rounded-full px-2 py-0.5">{itemCount}</span>}
            </Link>
            {user ? (
              <>
                <Link to="/mes-commandes" onClick={() => setOpen(false)} className="block py-2.5 text-base">Mes commandes</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="block py-2.5 text-base text-[#E8112D]">Se déconnecter</button>
              </>
            ) : (
              <>
                <Link to="/compte/connexion" onClick={() => setOpen(false)} className="block py-2.5 text-base">Se connecter</Link>
                <Link
                  to="/compte/inscription"
                  onClick={() => setOpen(false)}
                  className="mt-2 block text-center rounded-full bg-[#F5A800] px-5 py-3 text-sm font-semibold text-[#16241B]"
                >
                  Créer un compte
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
